import { z } from 'zod';
import { LanguageSchema, RoleSchema } from './enums.js';

/**
 * User-field rule constants. Single source for both the contract schemas
 * below and client-side localized validation (apps/web).
 */
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 32;
export const USERNAME_REGEX = /^[A-Za-z0-9._-]+$/;
export const PASSWORD_MIN = 10;
export const PASSWORD_LETTER_REGEX = /[A-Za-z]/;
export const PASSWORD_DIGIT_REGEX = /\d/;
export const NAME_MIN = 2;
export const NAME_MAX = 100;

/** Username chars: letters, digits, dot, underscore, hyphen, 3..32 */
export const UsernameSchema = z
  .string()
  .min(USERNAME_MIN)
  .max(USERNAME_MAX)
  .regex(USERNAME_REGEX, 'Username may contain only letters, digits, ., _, -');

/** Password: 10+ chars, at least one letter and one digit */
export const PasswordSchema = z
  .string()
  .min(PASSWORD_MIN, 'Password must be at least 10 characters')
  .regex(PASSWORD_LETTER_REGEX, 'Password must contain a letter')
  .regex(PASSWORD_DIGIT_REGEX, 'Password must contain a digit');

export const SignUpInputSchema = z
  .object({
    username: UsernameSchema,
    password: PasswordSchema,
    name: z.string().min(NAME_MIN).max(NAME_MAX),
    preferredLanguage: LanguageSchema.default('ro'),
  })
  .strict();
export type SignUpInput = z.infer<typeof SignUpInputSchema>;

export const SignInInputSchema = z
  .object({
    username: UsernameSchema,
    password: z.string().min(1),
  })
  .strict();
export type SignInInput = z.infer<typeof SignInInputSchema>;

/**
 * Canonical multi-value forestry entity list per user (2026-04-30).
 * Cap of 20 elements mirrors the DB CHECK constraint.
 * Each element: trimmed, 1..100 chars.
 */
export const EntitateSilvicaListSchema = z
  .array(z.string().trim().min(1).max(100))
  .max(20, 'Maximum 20 forestry entities per user');
export type EntitateSilvicaList = z.infer<typeof EntitateSilvicaListSchema>;

export const UserPublicSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  fullName: z.string(),
  cod: z.string().nullable().optional(),
  role: RoleSchema,
  preferredLanguage: LanguageSchema,
  isActive: z.boolean(),
  managerId: z.string().uuid().nullable().optional(),
  email: z.string().nullable().optional(),
  emailNotifications: z.boolean().optional(),
  /** Push/in-app notification opt-out (ADR-016). Defaults true. */
  notificationsEnabled: z.boolean().optional(),
  /** Canonical multi-value field. Order is meaningful: list[0] = default. */
  assignedEntitatiSilvice: EntitateSilvicaListSchema.default([]),
  /**
   * @deprecated 2026-04-30 — read-only mirror of `assignedEntitatiSilvice[0] ?? null`.
   * Removed in release +2. New code MUST read assignedEntitatiSilvice.
   */
  assignedEntitateSilvica: z.string().nullable().optional(),
  defaultRaion: z.string().nullable().optional(),
  defaultPrimarie: z.string().nullable().optional(),
  defaultUcf: z.string().nullable().optional(),
  defaultOcolSilvic: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type UserPublic = z.infer<typeof UserPublicSchema>;

export const SessionPublicSchema = z.object({
  id: z.string().uuid(),
  expiresAt: z.string(),
});
export type SessionPublic = z.infer<typeof SessionPublicSchema>;

export const GetSessionResponseSchema = z.object({
  user: UserPublicSchema.nullable(),
  session: SessionPublicSchema.nullable(),
});
export type GetSessionResponse = z.infer<typeof GetSessionResponseSchema>;
